/**
 * Backend contract for strips: each strip is its OWN trial_plot (`columns: 1`,
 * `is_strip: true`) whose geometry is a `MultiPolygon` (the strip's disjoint
 * pieces), with that strip's own `properties`. The backend does NOT accept a
 * GeometryCollection or a single group plot.
 *
 * In the app we keep strips as ONE group (for rendering/colouring/labels/rates),
 * so this transform runs ONLY on the outgoing save payload: it splits the one
 * strip-group plot into N single-strip plots. Nothing in the in-app state
 * (newTrialState) is changed.
 */

const zeroMultiPolygon = (): any => ({
  type: "MultiPolygon",
  coordinates: [
    [
      [
        [0, 0],
        [0, 0],
        [0, 0],
        [0, 0],
      ],
    ],
  ],
});

/** Coerce a cell geometry to a MultiPolygon (backend always wants MultiPolygon). */
const toMultiPolygon = (geom: any): any => {
  if (geom?.type === "MultiPolygon" && Array.isArray(geom.coordinates)) {
    return { type: "MultiPolygon", coordinates: geom.coordinates };
  }
  if (geom?.type === "Polygon" && Array.isArray(geom.coordinates)) {
    return { type: "MultiPolygon", coordinates: [geom.coordinates] };
  }
  return zeroMultiPolygon();
};

/**
 * One MultiPolygon per strip, extracted from the group's geometry. The in-app
 * group geometry is a GeometryCollection (member i = strip i). A legacy flat
 * MultiPolygon is handled best-effort (one polygon per strip).
 */
const extractStripGeometries = (geometry: any, count: number): any[] => {
  if (
    geometry?.type === "GeometryCollection" &&
    Array.isArray(geometry.geometries)
  ) {
    return geometry.geometries.map(toMultiPolygon);
  }
  if (
    geometry?.type === "MultiPolygon" &&
    Array.isArray(geometry.coordinates)
  ) {
    return geometry.coordinates.map((polygonCoords: any) => ({
      type: "MultiPolygon",
      coordinates: [polygonCoords],
    }));
  }
  return Array.from({ length: count }, zeroMultiPolygon);
};

/** Slice the group's combined properties down to a single strip's properties. */
const sliceStripProperties = (properties: any, stripIndex: number): any => {
  const out: any = {};

  const seeds = properties?.seeds;
  if (seeds?.rates_and_dosages) {
    out.seeds = {
      measure_unit: seeds.measure_unit,
      rates_and_dosages: [seeds.rates_and_dosages[stripIndex]].filter(Boolean),
    };
  }

  const fertilisers = properties?.fertilisers;
  if (fertilisers?.rates_and_dosages) {
    out.fertilisers = {
      measure_unit: fertilisers.measure_unit,
      rates_and_dosages: [fertilisers.rates_and_dosages[stripIndex]].filter(
        Boolean
      ),
    };
  }

  const biologicals = properties?.biologicals;
  if (Array.isArray(biologicals?.treatments)) {
    out.biologicals = {
      measure_unit: biologicals.measure_unit,
      rate: biologicals.rate,
      // treatments is [application][plot]; keep only this strip per application.
      treatments: biologicals.treatments.map((application: any[]) =>
        Array.isArray(application)
          ? application
              .filter(
                (cell, index) =>
                  cell != null && (cell.plot_cell_id ?? index) === stripIndex
              )
              .map((cell) =>
                cell.plot_cell_id != null ? { ...cell, plot_cell_id: 0 } : cell
              )
          : []
      ),
    };
  }

  return out;
};

/**
 * Replace every strip-group plot in the save payload with N single-strip plots
 * (one MultiPolygon each). Non-strip plots pass through unchanged.
 */
export const splitStripGroupsIntoPlots = (trialPlots: any[]): any[] => {
  if (!Array.isArray(trialPlots)) return trialPlots;

  const result: any[] = [];
  trialPlots.forEach((trialPlot) => {
    const plot = trialPlot?.plot;
    const totalStrips = plot?.totalStrips;

    if (!plot?.isStripPlot || !totalStrips || totalStrips < 1) {
      result.push(trialPlot);
      return;
    }

    const stripGeometries = extractStripGeometries(
      plot?.geojson?.geometry,
      totalStrips
    );

    // Drop group-only fields; each split plot is a standalone 1×1 strip plot.
    const {
      totalStrips: _totalStrips,
      stripIndices: _stripIndices,
      stripPolygonCounts: _stripPolygonCounts,
      id: _id,
      plot_id: _plotId,
      ...restPlot
    } = plot;

    for (let stripIndex = 0; stripIndex < totalStrips; stripIndex++) {
      result.push({
        ...trialPlot,
        plot: {
          ...restPlot,
          name: `${String(plot.name ?? "Strip")} ${stripIndex + 1}`,
          rows: 1,
          columns: 1,
          isStripPlot: true,
          is_strip: true,
          geojson: {
            type: "Feature",
            geometry: stripGeometries[stripIndex] ?? zeroMultiPolygon(),
            properties: {},
          },
        },
        replicants: [],
        properties: sliceStripProperties(trialPlot?.properties, stripIndex),
      });
    }
  });

  return result;
};

// ---------------------------------------------------------------------------
// LOAD: the inverse of the split. The backend returns N single-strip plots;
// the in-app model wants ONE strip group. Merge them back before the app reads
// the loaded trial, so rendering/colouring/labels/rates all see one group.
// ---------------------------------------------------------------------------

/** A loaded plot is a strip if the backend `is_strip` (or our `isStripPlot`) is set. */
const isStripEntry = (trialPlot: any): boolean =>
  trialPlot?.plot?.is_strip === true || trialPlot?.plot?.isStripPlot === true;

/** Re-assemble the group's combined properties from N single-strip plots. */
const mergeStripProperties = (stripPlots: any[]): any => {
  const first = stripPlots[0]?.properties ?? {};
  const out: any = {};

  if (first.seeds) {
    out.seeds = {
      measure_unit: first.seeds.measure_unit,
      rates_and_dosages: stripPlots.flatMap(
        (sp) => sp?.properties?.seeds?.rates_and_dosages ?? []
      ),
    };
  }

  if (first.fertilisers) {
    out.fertilisers = {
      measure_unit: first.fertilisers.measure_unit,
      rates_and_dosages: stripPlots.flatMap(
        (sp) => sp?.properties?.fertilisers?.rates_and_dosages ?? []
      ),
    };
  }

  const biologicals = stripPlots.find(
    (strip) => strip?.properties?.biologicals != null
  )?.properties.biologicals;
  if (biologicals) {
    const applicationCount = Math.max(
      0,
      ...stripPlots.map((strip) => {
        const applications = strip?.properties?.biologicals?.treatments;
        return Array.isArray(applications) ? applications.length : 0;
      })
    );
    // treatments is [application][plot]; each strip contributes one plot column.
    const treatments = [];
    for (let app = 0; app < applicationCount; app++) {
      treatments.push(
        stripPlots.map((strip, stripIndex) => {
          const cell = strip?.properties?.biologicals?.treatments?.[app]?.[0];
          return cell?.plot_cell_id != null
            ? { ...cell, plot_cell_id: stripIndex }
            : cell ?? null;
        })
      );
    }
    out.biologicals = {
      measure_unit: biologicals.measure_unit,
      rate: biologicals.rate,
      treatments,
    };
  }

  return out;
};

/**
 * Collapse all single-strip plots into ONE group plot (`columns: N`,
 * `isStripPlot: true`, GeometryCollection geometry), inserted at the position
 * of the first strip plot. Non-strip plots pass through unchanged; a trial with
 * no strip plots is returned untouched.
 */
export const mergeStripPlotsIntoGroup = (
  trialPlots: any[],
  isStripTrial = false
): any[] => {
  if (!Array.isArray(trialPlots)) return trialPlots;

  // A strip trial contains only strip plots, so the trial-level flag lets us
  // merge even if the backend doesn't echo `is_strip` on each plot.
  const isStrip = (trialPlot: any): boolean =>
    isStripTrial || isStripEntry(trialPlot);

  const stripPlots = trialPlots.filter(isStrip);
  if (stripPlots.length === 0) return trialPlots;

  const totalStrips = stripPlots.length;
  const base = stripPlots[0]?.plot ?? {};

  const group = {
    ...stripPlots[0],
    plot: {
      ...base,
      name: "Strips Group",
      rows: 1,
      columns: totalStrips,
      isStripPlot: true,
      is_strip: true,
      totalStrips,
      stripIndices: Array.from({ length: totalStrips }, (_, i) => i),
      geojson: {
        type: "Feature",
        geometry: {
          type: "GeometryCollection",
          geometries: stripPlots.map((sp) =>
            toMultiPolygon(sp?.plot?.geojson?.geometry)
          ),
        },
        properties: {},
      },
    },
    replicants: [],
    properties: mergeStripProperties(stripPlots),
  };

  const result: any[] = [];
  let inserted = false;
  trialPlots.forEach((trialPlot) => {
    if (isStrip(trialPlot)) {
      if (!inserted) {
        result.push(group);
        inserted = true;
      }
      return;
    }
    result.push(trialPlot);
  });

  return result;
};
